import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

/**
 * Reusable Mark-Repaid dialog. Drop anywhere — give it a `row`
 * ({ user_id, name, outstanding }) and it handles the rest.
 */
const MarkRepaidDialog = ({ open, row, onClose, onDone }) => {
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open && row) { setAmount(Number(row.outstanding || 0).toFixed(2)); setNote(''); }
    }, [open, row]);

    if (!row) return null;
    const max = Number(row.outstanding || 0);
    const amt = parseFloat(amount);
    const valid = amt > 0 && amt <= max + 0.001;

    const submit = async () => {
        if (!valid) return;
        setSubmitting(true);
        try {
            await axios.post(`${API}/admin/loans/mark-repaid`,
                { user_id: row.user_id, amount: amt, note: note.trim() || undefined },
                { withCredentials: true },
            );
            toast.success(`Recorded ${fmtMoney(amt)} repayment from ${row.name}`);
            onDone?.();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to record repayment');
        } finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="bg-[#121212] border-orange-500/30 max-w-md text-zinc-100" data-testid="mark-repaid-dialog">
                <DialogHeader>
                    <DialogTitle className="text-orange-400 flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5" /> Mark Loan Repaid — {row.name}
                    </DialogTitle>
                    <DialogDescription className="text-zinc-400">
                        Outstanding: <span className="font-mono font-bold text-orange-300">{fmtMoney(max)}</span>.
                        Records the repayment for bookkeeping only — does NOT touch the player&apos;s wallet credits.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-zinc-300">Amount repaid</Label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-orange-400 font-bold">$</span>
                            <Input type="number" step="0.01" min="0.01" max={max.toFixed(2)}
                                value={amount} onChange={(e) => setAmount(e.target.value)}
                                className="bg-zinc-950 border-zinc-700 text-zinc-100 pl-7 font-mono text-lg"
                                data-testid="mark-repaid-amount"
                            />
                        </div>
                        <div className="flex gap-1.5 mt-1.5">
                            <button type="button" onClick={() => setAmount(max.toFixed(2))}
                                className="px-2 py-1 text-[10px] rounded border border-zinc-700 text-zinc-400 hover:border-orange-500/50 hover:text-orange-300">
                                Full {fmtMoney(max)}
                            </button>
                        </div>
                    </div>
                    <div>
                        <Label className="text-zinc-300">Note (optional)</Label>
                        <Input value={note} onChange={(e) => setNote(e.target.value)}
                            placeholder="e.g. paid cash 3:15 PM" maxLength={120}
                            className="bg-zinc-950 border-zinc-700 text-zinc-100"
                            data-testid="mark-repaid-note"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button onClick={submit} disabled={!valid || submitting}
                        className="bg-orange-500 hover:bg-orange-400 text-zinc-900 font-black"
                        data-testid="mark-repaid-submit">
                        {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Recording…</> : `Record ${amount ? fmtMoney(amt) : ''}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default MarkRepaidDialog;
